import { indentsApi } from '../api/modules/indents.api';
import { getApiErrorMessage } from '../api/client';
import { USE_API } from '../config/api';
import { indentSeed } from '../data/master/indents';
import { mapPaginated, indentFromApi, indentToApi } from '../models';
import { getEquipment } from './equipment.repository';

export async function listIndents(params = {}) {
  if (!USE_API) {
    let items = [...indentSeed];
    if (params.search) {
      const q = String(params.search).toLowerCase();
      items = items.filter(
        (i) =>
          i.indentId?.toLowerCase().includes(q) ||
          i.raisedByName?.toLowerCase().includes(q) ||
          i.vendorId?.toLowerCase().includes(q),
      );
    }
    if (params.status) {
      const s = String(params.status).toLowerCase();
      items = items.filter((i) => String(i.status).toLowerCase() === s);
    }
    const page = params.page || 1;
    const pageSize = params.pageSize || items.length || 10;
    const start = (page - 1) * pageSize;
    return {
      items: items.slice(start, start + pageSize),
      total: items.length,
      page,
      pageSize,
    };
  }
  try {
    const result = await indentsApi.list(params);
    return mapPaginated(result, indentFromApi);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load indents'));
  }
}

export async function getPendingIndentCount() {
  if (!USE_API) {
    const pending = indentSeed.filter(
      (i) => String(i.status || '').toLowerCase() === 'pending',
    ).length;
    return { pending };
  }
  try {
    const data = await indentsApi.pendingCount();
    return { pending: Number(data?.pending) || 0 };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load pending indent count'));
  }
}

export async function getIndent(id) {
  if (!USE_API) {
    return indentSeed.find((i) => i.indentId === id) || null;
  }
  try {
    return await enrichIndentEquipment(indentFromApi(await indentsApi.getById(id)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load indent'));
  }
}

/** Fill recommendation/assignment equipment if API omitted nested relations. */
async function enrichIndentEquipment(indent) {
  if (!indent) return null;
  const ids = new Set();
  (indent.recommendations || []).forEach((r) => {
    if (!r.equipment && r.assetDbId) ids.add(Number(r.assetDbId));
  });
  if (indent.assignment && !indent.assignment.equipment && indent.assignment.assetDbId) {
    ids.add(Number(indent.assignment.assetDbId));
  }
  if (ids.size === 0) return indent;

  const entries = await Promise.all(
    [...ids].map(async (equipmentId) => {
      try {
        const equipment = await getEquipment(equipmentId);
        return [equipmentId, equipment];
      } catch {
        return [equipmentId, null];
      }
    }),
  );
  const byId = Object.fromEntries(entries);

  return {
    ...indent,
    recommendations: (indent.recommendations || []).map((r) => {
      const key = Number(r.assetDbId);
      if (r.equipment || !r.assetDbId || !byId[key]) return r;
      const equipment = byId[key];
      return {
        ...r,
        equipment,
        assetId: equipment.assetId || r.assetId,
        equipmentName:
          equipment.equipmentDescription || equipment.equipmentType || r.equipmentName,
        equipmentType: equipment.equipmentType || r.equipmentType,
        regNo: equipment.regNo || r.regNo,
        manufacturer: equipment.manufacturer || r.manufacturer,
        model: equipment.model || r.model,
      };
    }),
    assignment:
      indent.assignment && !indent.assignment.equipment && indent.assignment.assetDbId
        ? {
            ...indent.assignment,
            equipment: byId[Number(indent.assignment.assetDbId)] || null,
            assetId:
              byId[Number(indent.assignment.assetDbId)]?.assetId ||
              indent.assignment.assetId,
          }
        : indent.assignment,
  };
}

export async function createIndent(ui) {
  if (!USE_API) throw new Error('Create indent requires API mode');
  try {
    return indentFromApi(await indentsApi.create(indentToApi(ui)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to create indent'));
  }
}

export async function recommendIndent(id, body) {
  if (!USE_API) throw new Error('Recommend requires API mode');
  try {
    return await enrichIndentEquipment(
      indentFromApi(await indentsApi.recommend(id, body)),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to recommend equipment'));
  }
}

export async function assignIndent(id, body) {
  if (!USE_API) throw new Error('Assign requires API mode');
  try {
    return await enrichIndentEquipment(indentFromApi(await indentsApi.assign(id, body)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to assign equipment'));
  }
}

export async function releaseIndent(id, body) {
  if (!USE_API) throw new Error('Release requires API mode');
  try {
    return await enrichIndentEquipment(indentFromApi(await indentsApi.release(id, body)));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to release equipment'));
  }
}

export async function cancelIndent(id) {
  if (!USE_API) throw new Error('Cancel requires API mode');
  try {
    return indentFromApi(await indentsApi.cancel(id));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to cancel indent'));
  }
}

export async function deleteIndent(id) {
  if (!USE_API) throw new Error('Delete indent requires API mode');
  try {
    await indentsApi.remove(id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete indent'));
  }
}
